import React, { useState, useEffect } from 'react';

export default function TabbedContent({ htmlContent }) {
  const [activeTab, setActiveTab] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [styles, setStyles] = useState('');

  useEffect(() => {
    // Parse the HTML to extract tabs, content, and styles
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    // Extract all style tags and scope them to .tabbed-container
    const styleTags = doc.querySelectorAll('style');
    let allStyles = '';
    styleTags.forEach(styleTag => {
      let scopedStyles = styleTag.textContent;
      // Scope all styles to .tabbed-container to prevent global leaks
      // Skip body, html, * selectors as they affect the whole page
      scopedStyles = scopedStyles
        .split('}')
        .map(rule => {
          if (!rule.trim()) return '';
          const [selector, ...rest] = rule.split('{');
          const cleanSelector = selector.trim();

          // Skip global selectors that would affect the entire page
          if (['*', 'body', 'html'].includes(cleanSelector)) {
            return '';
          }

          // Scope the selector to .tabbed-container
          return `.tabbed-container ${cleanSelector}{${rest.join('{')}`;
        })
        .filter(rule => rule.trim())
        .join('}') + '}';

      allStyles += scopedStyles + '\n';
    });
    setStyles(allStyles);

    const tabElements = doc.querySelectorAll('.tab');
    const extractedTabs = [];

    tabElements.forEach((tab, index) => {
      // Try to get data-tab attribute first, otherwise use index as fallback
      let tabId = tab.getAttribute('data-tab');

      // If no data-tab, look for corresponding tab-content by ID pattern
      if (!tabId) {
        // Try to find associated content element
        // Look for elements with class "tab-content" and match by position
        const allTabContents = doc.querySelectorAll('.tab-content');
        if (allTabContents[index]) {
          tabId = allTabContents[index].id || `tab-${index}`;
        } else {
          tabId = `tab-${index}`;
        }
      }

      const tabLabel = tab.textContent;

      // Try to find content by ID first (for both id-based and data-tab based)
      let contentElement = doc.getElementById(tabId);

      // If not found by ID, look for tab-content by index position
      if (!contentElement) {
        const allTabContents = doc.querySelectorAll('.tab-content');
        contentElement = allTabContents[index];
      }

      const contentHtml = contentElement ? contentElement.innerHTML : '';

      extractedTabs.push({
        id: tabId,
        label: tabLabel,
        content: contentHtml,
        isActive: tab.classList.contains('active')
      });
    });

    setTabs(extractedTabs);
    if (extractedTabs.length > 0) {
      setActiveTab(extractedTabs[0].id);
    }
  }, [htmlContent]);

  if (tabs.length === 0) {
    // If no tabs found, just render the HTML as-is
    return <section className="info-section" dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  }

  return (
    <>
      {styles && <style>{styles}</style>}
      <div className="tabbed-container">
        <div className="tabs-header">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="tab-content-wrapper">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`tab-pane ${activeTab === tab.id ? 'active' : ''}`}
              dangerouslySetInnerHTML={{ __html: tab.content }}
            />
          ))}
        </div>
      </div>
    </>
  );
}
